import {
  PolymorphicForwardedRef,
  PolymorphicProps,
} from '@axa-ch/react-polymorphic-types';

import { cn } from '~/lib/utils/classname';
import { formatPrice } from '~/lib/utils/properties';

import Wrap from './Wrap';

export type ClassSlots = 'main' | 'before' | 'sup' | 'after';

export const defaultElement: React.ElementType = 'data' as const;
export type AllowedElements =
  | typeof defaultElement
  | keyof React.JSX.IntrinsicElements;

export type OwnProps<T extends AllowedElements> =
  React.ComponentPropsWithoutRef<T> & {
    value?: number | null;
    supText?: string | null;
    beforeText?: string | React.ReactNode | null;
    afterText?: string | React.ReactNode | null;

    className?: string;
    classNames?: {
      [key in ClassSlots]?: string;
    };

    ref?: PolymorphicForwardedRef<T>;
  };

export type Props<T extends AllowedElements> = PolymorphicProps<
  OwnProps<T>,
  T,
  AllowedElements
>;

const Price = <E extends AllowedElements = typeof defaultElement>({
  as: asComponent,

  value,
  supText,
  beforeText,
  afterText,

  className,
  classNames,
}: Props<E>) => {
  const Component = asComponent || defaultElement;
  const formattedValue = formatPrice(value);

  return (
    formattedValue && (
      <>
        <Component
          className={className}
          itemProp="price"
          value={value?.toString()}
          content={value?.toString()}
        >
          {beforeText && (
            <span className={classNames?.before}>&nbsp;{beforeText}</span>
          )}

          <Wrap
            with={(children) => (
              <span className={classNames?.main}>
                {children}

                {supText && (
                  <sup
                    className={cn(
                      'text-xs lowercase [font-variant:small-caps]',
                      classNames?.sup
                    )}
                  >
                    &nbsp;{supText}
                  </sup>
                )}
              </span>
            )}
            when={Boolean(supText || afterText)}
          >
            {formattedValue}
          </Wrap>

          {afterText && (
            <span className={classNames?.after}>&nbsp;{afterText}</span>
          )}
        </Component>

        <meta itemProp="priceCurrency" content="EUR"></meta>
      </>
    )
  );
};

export default Price;
